import { faBell, faBellSlash, faExternalLink, faUserPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useContext } from 'react';

import Debug from '@/components/Debug';
import NoteBody from '@/components/NoteBody';
import AppContext from '@/contexts/appContext';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import PersonContext from '@/pages/Person/PersonContext';
import { AppointmentTypeWithScheduleExpectationCycles } from '@/types/appointmentType';
import { ScheduleExpectationCycle as IScheduleExpectationCycle } from '@/types/scheduleExpectation';
import { Button as ButtonV2, Card, DualTime, DualTimeRange, Pill, Tooltip, Typography } from '@/ui';
import AppointmentAttendancePills from '@/ui/components/AppointmentAttendancePills';
import formatStaffName from '@/utils/formatStaffName';
import isStandaloneCustomExpectation from '@/utils/isStandaloneCustomExpectation';
import { APPOINTMENTS_BOOKING_UI, hasPermission } from '@/utils/userPermission';

import { useSilenceScheduleExpectationCycleButton } from './SilenceScheduleExpectationCycleButton';

const leftBorderMap = {
  silenced: 'border-l-blue-400',
  muted: 'border-l-uniform-gray-300 dark:border-l-uniform-gray-600',
  met: 'border-l-green-500',
  missedReschedule: 'border-l-yellow-500 dark:border-l-yellow-400',
  unmet: 'border-l-red-400'
} as const;

const ScheduleExpectationCycle = ({
  isMostRecent: _isMostRecent,
  memberTimeZone,
  onSeeExpectationDetails,
  phaseId: _phaseId,
  scheduleExpectationCycle,
  appointmentType,
  uiMode = 'default',
  enableSilenceCycle = true
}: {
  isMostRecent: boolean;
  memberTimeZone: string | null;
  onSeeExpectationDetails?: (appointmentTypeId: string, expectationId: string) => void;
  phaseId: string;
  scheduleExpectationCycle: IScheduleExpectationCycle;
  appointmentType: AppointmentTypeWithScheduleExpectationCycles;
  uiMode?: 'default' | 'muted';
  enableSilenceCycle?: boolean;
}) => {
  const { permissions } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const { navigateToAppointment, navigateToBooking } = useNavigateToPage();
  const isMutedUIMode = uiMode === 'muted';

  const {
    appointment_attendances,
    cycle_expectation_met,
    cycle_start_at,
    cycle_end_at,
    appointments_scheduled,
    expected_this_cycle,
    missed_opportunities,
    missed_reschedules,
    silenced_at,
    silenced_by,
    silenced_note
  } = scheduleExpectationCycle;

  const isSilenced = silenced_at !== null;
  const hasMissedOpportunities = (missed_opportunities ?? 0) > 0;
  const hasMissedReschedules = (missed_reschedules ?? 0) > 0;
  // Missed opportunities are the more severe shortfall, so they keep the red treatment
  // whenever a cycle has both.
  const isMissedRescheduleOnly = hasMissedReschedules && !hasMissedOpportunities;

  // cycle_end_at of one cycle equals cycle_start_at of the next, so the displayed end date
  // is the day before to avoid showing the same date for both boundaries.
  const cycleDisplayEndAt = new Date(new Date(cycle_end_at).getTime() - 24 * 60 * 60 * 1000);

  const { buttonProps: silenceButtonProps, modal: silenceModal } =
    useSilenceScheduleExpectationCycleButton(scheduleExpectationCycle);

  const showBookingButton =
    !isSilenced &&
    appointments_scheduled < expected_this_cycle &&
    !!appointmentType.appointment_type_healthie_id &&
    hasPermission({
      requiredPermission: APPOINTMENTS_BOOKING_UI,
      permissions: permissions
    });

  const buildBookingParams = () => {
    const params = new URLSearchParams();
    params.append('appointmentType', appointmentType.appointment_type_healthie_id.toString());
    if (appointmentType.staff_relationship && appointmentType.staff_relationship.healthie_id) {
      params.append('providerIds', appointmentType.staff_relationship.healthie_id.toString());
    }
    params.append('startDate', new Date(cycle_start_at).toISOString());
    params.append('endDate', new Date(new Date(cycle_end_at).getTime() - 1000).toISOString());

    const schedulingTargets = scheduleExpectationCycle.cycle_data?.scheduling_targets;
    if (schedulingTargets && schedulingTargets.length > 0) {
      const nextPosition = appointments_scheduled + 1;
      const target =
        schedulingTargets.find((t) => t.position === nextPosition && t.actual_datetime === null) ??
        schedulingTargets.find((t) => t.actual_datetime === null);
      if (target) {
        params.set('bookingConstraints', 'true');
        params.set('targetDate', target.target_date);
        params.set('targetMode', target.constraint);
        params.set('targetConstraint', target.mode);
      }
    }

    return params;
  };

  const statusKey = isSilenced
    ? 'silenced'
    : isMutedUIMode
      ? 'muted'
      : cycle_expectation_met
        ? 'met'
        : isMissedRescheduleOnly
          ? 'missedReschedule'
          : 'unmet';

  const expectationId =
    scheduleExpectationCycle.schedule_expectation?.custom_schedule_expectation_id ??
    scheduleExpectationCycle.schedule_expectation?.schedule_expectation_id;
  const isCustomExpectation = isStandaloneCustomExpectation(
    scheduleExpectationCycle.schedule_expectation
  );

  return (
    <>
      <Card className={`mb-3 border-l-4 ${leftBorderMap[statusKey]}`} padding="sm">
        {silenceModal}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <DualTimeRange
              startDateTime={new Date(cycle_start_at)}
              endDateTime={cycleDisplayEndAt}
              memberTZ={memberTimeZone}
              className="text-sm font-medium text-uniform-gray-800 dark:text-uniform-gray-200"
            />
            <div className="flex flex-wrap gap-1">
              {isSilenced ? (
                <Pill size="sm" color="secondary" icon={faBellSlash}>
                  Silenced
                </Pill>
              ) : (
                <>
                  {appointments_scheduled < expected_this_cycle && (
                    <Pill size="sm" color={isMissedRescheduleOnly ? 'warning' : 'destructive'}>
                      Underscheduled
                    </Pill>
                  )}
                  {hasMissedOpportunities && (
                    <Pill size="sm" color="destructive">
                      Missed opportunity
                    </Pill>
                  )}
                  {hasMissedReschedules && (
                    <Pill size="sm" color="warning">
                      Missed reschedule
                    </Pill>
                  )}
                  {appointments_scheduled > expected_this_cycle && (
                    <Pill size="sm" color="destructive">
                      Overscheduled
                    </Pill>
                  )}
                  {cycle_expectation_met && (
                    <Pill size="sm" color="primary">
                      Met
                    </Pill>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
              {appointments_scheduled} of {expected_this_cycle} scheduled
            </span>
            <div className="flex items-center gap-1">
              {isCustomExpectation && (
                <Tooltip
                  content="This is a custom expectation"
                  triggerClassName="inline-flex items-center">
                  <FontAwesomeIcon
                    icon={faUserPen}
                    className="text-xs text-uniform-gray-400 dark:text-uniform-gray-500 mx-1"
                    aria-label="Custom expectation"
                  />
                </Tooltip>
              )}
              {onSeeExpectationDetails && expectationId && (
                <ButtonV2
                  variant="subtle"
                  color="secondary"
                  size="xs"
                  aria-label="See expectation details"
                  onClick={() =>
                    onSeeExpectationDetails(appointmentType.appointment_type_id, expectationId)
                  }>
                  <FontAwesomeIcon icon={faExternalLink} />
                  <span className="hidden md:inline">See expectation details</span>
                </ButtonV2>
              )}
              {enableSilenceCycle && (
                <ButtonV2
                  variant="ghost"
                  color="neutral"
                  size="xs"
                  disabled={silenceButtonProps.disabled}
                  onClick={() => silenceButtonProps.onClick?.()}
                  aria-label={isSilenced ? 'Unsilence cycle' : 'Silence cycle'}>
                  <FontAwesomeIcon icon={isSilenced ? faBell : faBellSlash} />
                  <span className="hidden md:inline">{isSilenced ? 'Unsilence' : 'Silence'}</span>
                </ButtonV2>
              )}
            </div>
          </div>
        </div>

        {isSilenced && (
          <div className="mt-2 flex flex-col gap-1">
            <Typography variant="caption" muted as="p">
              Silenced{' '}
              <DualTime
                dateTime={new Date(silenced_at)}
                memberTZ={memberTimeZone}
                className="text-xs text-uniform-gray-600 dark:text-uniform-gray-400"
              />
              {silenced_by && <> by {formatStaffName({ staff: silenced_by })}</>}
            </Typography>
            {silenced_note?.body && (
              <Card padding="sm">
                <NoteBody note={silenced_note} />
              </Card>
            )}
          </div>
        )}

        {(appointment_attendances.length > 0 || showBookingButton) && (
          <hr className="my-2 border-uniform-gray-100 dark:border-uniform-gray-700" />
        )}

        {appointment_attendances.length > 0 && (
          <ul className="space-y-1.5">
            {appointment_attendances.map((appointmentAttendance) => {
              const isDifferentType =
                appointmentAttendance.appointment_title !== appointmentType.appointment_type_name;
              return (
                <li key={appointmentAttendance.appointment_attendance_id}>
                  <Card className="w-fit" padding="xs">
                    <AppointmentAttendancePills
                      status={appointmentAttendance.internal_status}
                      appointmentTypeName={
                        isDifferentType ? appointmentAttendance.appointment_title : undefined
                      }
                      dateTime={new Date(appointmentAttendance.start_at)}
                      memberTZ={memberTimeZone}
                      appointment={appointmentAttendance.appointment}
                      chartingCompleted={appointmentAttendance.charting_completed}
                      chartingStatus={appointmentAttendance.charting_status}
                      action={
                        <button
                          type="button"
                          onClick={() =>
                            navigateToAppointment(person.id, appointmentAttendance.appointment_id)
                          }>
                          <span className="sr-only">View appointment</span>
                          <FontAwesomeIcon icon={faExternalLink} />
                        </button>
                      }
                    />
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        {showBookingButton && (
          <div className="mt-2 flex justify-end">
            <ButtonV2
              variant="default"
              color="primary"
              size="xs"
              className="w-full sm:w-auto"
              onClick={() => {
                const params = buildBookingParams();
                navigateToBooking(person.id, params);
              }}>
              Book Appointment
            </ButtonV2>
          </div>
        )}
      </Card>
      <Debug object={scheduleExpectationCycle} />
    </>
  );
};

export default memo(ScheduleExpectationCycle);
